import React, { useEffect, useRef, useState } from 'react'
import { Accordion, AccordionDetails, FormControl } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import IFText from 'Components/IFText/IFText'
import Styles from './ChargePointTransactionListItem.module.css'
import Colors from 'Theme/Colors'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import Moment from 'moment'
import icons from 'Theme/Icons'
import PropTypes from 'prop-types'
import InfinityEnums from 'Enums/InfinityEnums'
import MuiAccordionSummary from '@mui/material/AccordionSummary'
import { isPropsMatch } from 'Utils/PropsMatch'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import TransactionStatus from 'Components/TransactionStatus/TransactionStatus'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import IFButton from 'Components/IFButton/IFButton'
import { SelectPicker } from 'rsuite'
import IFModal from 'Components/IFModal/IFModal'
import RequestState from 'Enums/RequestState'
import TariffSelectPicker from 'Components/TariffSelectPicker/TariffSelectPicker'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChargingPeriodAccordion from 'Components/ChargingPeriodAccordion/ChargingPeriodAccordion'
import CostBreakdownAccordion from 'Components/CostBreakdownAccordion/CostBreakdownAccordion'
import { toNumberWithFallback } from './../../Utils/DataConversion'

const StyledHyperLink = styled.a`
  font-size: 14;
  vertical-align: middle;
  text-align: center;
  padding-bottom: 2px;
  margin-left: 6px;
  color: ${(props) => props.textColor};
  &:hover {
    color: ${(props) => props.hoverColor} !important;
  }
  &:before {
    content: '';
    width: 0;
    height: 0.1em;
    position: absolute;
    bottom: 0;
    left: 50%;
    background: ${(props) => props.hoverColor} !important;
    transition: all 0.15s;
  }
  &:hover:before {
    left: 0;
    background: ${(props) => props.hoverColor} !important;
  }
`
const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  '&.Mui-expanded': {
    minHeight: 48,
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '0px',
  },
}))
const customDialogContentStyle = {
  width: '35rem',
}
function ChargePointTransactionListItem({
  connectorUid,
  connectorType,
  energyConsumption,
  startedAt,
  endedAt,
  processedAt,
  startingChargingToken,
  amount,
  status,
  startingChargingTokenType,
  currency,
  handleOpenChartModal,
  sessionID,
  startingChargingTokenId,
  chargingPeriods,
  onReverseClick,
  reverseTransactionRequestState,
  tariffsList,
  transactionTariff,
  operatorUser,
  costBreakdown,
  ...props
}) {
  const reverseRef = useRef()
  const [tokenHover, setTokenHover] = useState()
  const [isHuidCopied, setIsHuidCopied] = useState(false)
  const animateUid = () => {
    setIsHuidCopied(!isHuidCopied)
    setTimeout(() => setIsHuidCopied(false), 2000)
  }
  const durationHr = parseInt(Moment(endedAt).diff(startedAt, 'hours'))
  const durationMin = Moment.utc(
    Moment(endedAt, 'HH:mm:ss').diff(Moment(startedAt, 'HH:mm:ss')),
  ).format('mm')
  const { t } = useTranslation()
  const IconSrc = () => {
    switch (connectorType) {
      case InfinityEnums.ConnectorType.IEC_62196_T2:
        return icons.type2({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.CHADEMO:
        return icons.chademo({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T2_COMBO:
        return icons.comboCCS({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.DOMESTIC_F:
        return icons.schuko({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.GBT_DC:
        return icons.GBT_DC({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T1:
        return icons.type1({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.IEC_62196_T1_COMBO:
        return icons.type1CCS({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      case InfinityEnums.ConnectorType.GBT_AC:
        return icons.GBT_AC({
          width: 24,
          height: 24,
          color: Colors.black,
        })
      default:
        return icons.type2({
          width: 24,
          height: 24,
          color: Colors.black,
        })
    }
  }
  const [options, setOptions] = useState([
    { label: 'Reverse', value: 'Reverse' },
    { label: 'Correct', value: 'Correct' },
  ])
  const [action, setAction] = useState()
  const [tariff, setTariff] = useState(transactionTariff)

  const [jsonText, setJsonText] = useState(
    JSON.stringify(chargingPeriods, null, 2),
  )
  const [error, setError] = useState(false)

  const serializePeriod = (p) => {
    let startDateTime = null
    if (p.startTime instanceof Date) {
      startDateTime = p.startTime.toISOString()
    } else if (typeof p.startTime === 'string') {
      startDateTime = p.startTime
    }

    const nowIso = new Date().toISOString()

    const parkingTimeVolume = toNumberWithFallback(p.parkingTime)

    const dimensions = [
      {
        type: InfinityEnums.CdrDimensionType.TIME,
        volume: toNumberWithFallback(p.time),
      },
      {
        type: InfinityEnums.CdrDimensionType.ENERGY,
        volume: toNumberWithFallback(p.energy),
      },
      {
        type: InfinityEnums.CdrDimensionType.POWER,
        volume: toNumberWithFallback(p.power),
      },
      {
        type: InfinityEnums.CdrDimensionType.CURRENT,
        volume: toNumberWithFallback(p.current),
      },
      {
        type: InfinityEnums.CdrDimensionType.STATE_OF_CHARGE,
        volume: toNumberWithFallback(p.stateOfCharge),
      },
      ...(parkingTimeVolume !== 0
        ? [
            {
              type: InfinityEnums.CdrDimensionType.PARKING_TIME,
              volume: parkingTimeVolume,
            },
          ]
        : []),
    ]

    return {
      startDateTime,
      latestTimestamp: nowIso,
      statusLastUpdatedAt: nowIso,
      dimensions,
      connectorStatus: p.connectorStatus || 'Charging',
    }
  }
  const handlePeriodsChange = (periods) => {
    const payload = periods.map(serializePeriod)
    setJsonText(JSON.stringify(payload, null, 2))
  }
  let parsedPeriods = []

  try {
    parsedPeriods = JSON.parse(jsonText)
  } catch (e) {
    parsedPeriods = []
  }
  const handleCloseIcon = () => {
    setAction(null)
    setJsonText(JSON.stringify(chargingPeriods, null, 2))
    setError(false)
    setTariff()
  }

  const handleSubmit = () => {
    onReverseClick(action, jsonText, tariff ? tariff : transactionTariff)
  }

  useEffect(() => {
    if (reverseTransactionRequestState === RequestState.SUCCEEDED) {
      reverseRef?.current?.dismiss()
      setAction(null)
      setJsonText(JSON.stringify(chargingPeriods, null, 2))
      setError(false)
      setTariff()
    }
  }, [reverseTransactionRequestState])
  return (
    <div {...props}>
      <Accordion
        className={Styles.accordion}
        style={{
          boxShadow: `0px 0px 0px ${Colors.ChargePointLogTransactionItemBoxShadow}`,
          borderRadius: '0px',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={[Styles.accordionSummary]}
          sx={{
            '&:hover': {
              backgroundColor: Colors.ChargePointLogListItemHover,
            },
          }}
        >
          <div className={Styles.container}>
            <TransactionStatus status={status} />
            <div className={Styles.accordionIconWrapper}>
              {IconSrc()}
              <div className={Styles.uidContainer}>
                <IFText className={Styles.accordionLeftText}>
                  {`#${connectorUid}`}
                </IFText>
              </div>
            </div>
            <div className={Styles.accordionTextPower}>
              <div className={Styles.flashIcon}>{icons.flash}</div>
              <IFText className={Styles.accordionMiddleText}>
                {`${energyConsumption / 1000} kWh`}
              </IFText>
            </div>
            <div className={Styles.accordionTextDuration}>
              <TimerOutlinedIcon
                style={{ height: '18px' }}
                className={Styles.accordionicon}
              />
              <IFText className={Styles.accordionMiddleText}>
                {durationHr !== 0 ? `${durationHr}h` : ''}
                {durationMin === '00' && durationHr !== 0
                  ? ''
                  : ` ${durationMin}m`}
              </IFText>
            </div>
            <div className={Styles.accordionDateContainer}>
              <IFText className={Styles.accordionDate}>
                {Moment(processedAt).format('MMM DD, yyy \xa0 HH:mm:ss')}
              </IFText>
            </div>
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <div className={Styles.detailsColumn}>
            <div className={Styles.container}>
              <div className={Styles.leftDetails}>
                <div className={Styles.accordionDetails}>
                  <AccessTimeIcon className={Styles.accessTimeIcon} />
                  <div className={Styles.expandedLeftText}>
                    <div>
                      <IFText className={Styles.semiBold}>{'Start'}</IFText>
                      <IFText className={Styles.semiBold}> {'End'}</IFText>
                    </div>
                    <div>
                      <IFText className={Styles.accordionLeftText}>
                        {Moment(startedAt).format('MMM DD, yyy \xa0 HH:mm:ss')}
                      </IFText>
                      <IFText className={Styles.accordionLeftText}>
                        {Moment(endedAt).format('MMM DD, yyy \xa0 HH:mm:ss')}
                      </IFText>
                    </div>
                  </div>
                </div>
                <div className={Styles.costBreakdownWrapper}>
                  <CostBreakdownAccordion
                    costBreakdown={costBreakdown}
                    currency={currency}
                  />
                </div>
              </div>

              <div className={Styles.rightDetailsContainer}>
                <div className={Styles.expandedRight}>
                  <IFText className={Styles.currency}>{currency.code}</IFText>
                  <IFText className={Styles.accordionRightText}>
                    {`${Number(amount).toFixed(currency?.decimalPlaces)}`}
                  </IFText>
                </div>
                <div className={Styles.expandedRight}>
                  <CreditCardIcon />
                  <StyledHyperLink
                    hoverColor={Colors.primary}
                    textColor={Colors.text}
                    target="_blank"
                    style={{
                      cursor: 'pointer',
                      maxWidth: '100px',
                    }}
                    href={`/cards/${startingChargingTokenId}`}
                  >
                    <IFText className={Styles.accordionRightTextHyper}>
                      {`#${startingChargingToken}`}
                    </IFText>
                  </StyledHyperLink>
                  <div
                    className={Styles.UidCopyContainer}
                    onMouseEnter={() => setTokenHover(true)}
                    onMouseLeave={() => setTokenHover(false)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {tokenHover ? (
                      <div
                        className={Styles.iconContainer}
                        onClick={() => {
                          navigator.clipboard.writeText(startingChargingToken)
                        }}
                      >
                        <CheckIcon
                          className={
                            isHuidCopied ? Styles.visible : Styles.hide
                          }
                          style={{
                            color: Colors.primary,
                          }}
                        />
                        <ContentCopyIcon
                          onClick={animateUid}
                          style={{ color: Colors.ClipboardCopy }}
                          className={
                            !isHuidCopied ? Styles.visible : Styles.hide
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
                <div className={Styles.userContainer}>
                  <AnalyticsIcon sx={{ fontSize: 20, alignSelf: 'center' }} />

                  <IFText
                    style={{ color: Colors.text }}
                    className={Styles.middle}
                  >
                    <StyledHyperLink
                      hoverColor={Colors.primary}
                      textColor={Colors.text}
                      target="_blank"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleOpenChartModal(sessionID)}
                    >
                      {t('TransactionEvents.ChargingAnalytics')}
                    </StyledHyperLink>
                  </IFText>
                </div>
                {operatorUser.permissions.includes(
                  InfinityEnums.OperatorUserPermission.CAN_EDIT_TRANSACTION,
                ) && (
                  <IFButton
                    className={Styles.reverseButton}
                    color={Colors.primary}
                    text={t('UserTransaction.Reverse')}
                    onClick={() => reverseRef?.current?.show()}
                    size="xsm"
                  />
                )}
              </div>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <IFModal
        onDismiss={handleCloseIcon}
        maxWidth={'30rem'}
        title={t('UserTransaction.ReverseTitle')}
        ref={reverseRef}
        dialogContentStyle={customDialogContentStyle}
      >
        <div className={Styles.Container}>
          <div className={Styles.FormContainer}>
            <FormControl
              style={{
                boxShadow: `0px 0px 4px ${Colors.black} `,
              }}
              className={Styles.FormControl}
              onClick={(e) => e.stopPropagation()}
            >
              <SelectPicker
                preventOverflow={true}
                menuStyle={{
                  color: Colors.text,
                  zIndex: 1500,
                }}
                appearance="subtle"
                cleanable={false}
                searchable={false}
                onSelect={(value) => setAction(value)}
                data={options}
                value={action}
                className={Styles.selectPicker}
              />
            </FormControl>
            {action === 'Correct' && (
              <div className={Styles.JsonEditContainer}>
                <TariffSelectPicker
                  data={tariffsList}
                  appearance="default"
                  className={Styles.selectPicker}
                  height={'2.25rem'}
                  padding={'0.5rem'}
                  initialValue={transactionTariff}
                  onValueChange={(value) => {
                    setTariff(value?.value)
                  }}
                  currency={currency}
                  searchable={false}
                />
                <ChargingPeriodAccordion
                  periods={parsedPeriods}
                  onChange={handlePeriodsChange}
                  onErrorChange={(error) => {
                    setError(error)
                  }}
                />
              </div>
            )}
          </div>

          <div className={Styles.Button}>
            <IFButton
              text={t('UserTransaction.Submit')}
              color={Colors.primary}
              size={'sm'}
              isDead={!action || error}
              isLoading={
                reverseTransactionRequestState === RequestState.LOADING
              }
              onClick={() => {
                handleSubmit()
              }}
            ></IFButton>
          </div>
        </div>
      </IFModal>
    </div>
  )
}
ChargePointTransactionListItem.propTypes = {
  connectorUid: PropTypes.number.isRequired,
  connectorType: PropTypes.string,
  energyConsumption: PropTypes.number.isRequired,
  startedAt: PropTypes.string.isRequired,
  endedAt: PropTypes.string.isRequired,
  processedAt: PropTypes.string.isRequired,
  startingChargingToken: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  costBreakdown: PropTypes.object,
}

function shouldSkipRender(prevProps, nextProps) {
  return isPropsMatch(prevProps, nextProps, [
    'connectorUid',
    'connectorType',
    'energyConsumption',
    'startedAt',
    'endedAt',
    'processedAt',
    'startingChargingToken',
    'amount',
    'handleOpenChartModal',
  ])
}
export default React.memo(ChargePointTransactionListItem, shouldSkipRender)
